import type { AgentReport, AgentCheckResult } from './AgentResult';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import { SMIG } from '@shared/constants/Taux';

/**
 * Agent Anomalies : détecte les valeurs aberrantes.
 */
export class AgentAnomalies {
  static readonly NAME = 'Detecteur d anomalies';

  static run(salaries: readonly Salarie[], bulletins: readonly Bulletin[]): AgentReport {
    const start = performance.now();
    const r: AgentCheckResult[] = [];

    const sousSMIG = salaries.filter((s) => s.statut === 'actif' && s.salaire_base < SMIG);
    r.push(
      sousSMIG.length === 0
        ? { test: 'Salaires >= SMIG', level: 'ok', msg: 'Tous au-dessus du SMIG' }
        : {
            test: 'Salaires >= SMIG',
            level: 'warn',
            msg: `${sousSMIG.length} salariés sous le SMIG`,
          },
    );

    const salairesHauts = salaries.filter((s) => s.salaire_base > 10_000_000);
    r.push(
      salairesHauts.length === 0
        ? { test: 'Salaires plausibles', level: 'ok', msg: 'Aucun salaire > 10M' }
        : {
            test: 'Salaires plausibles',
            level: 'warn',
            msg: `${salairesHauts.length} salaires inhabituels`,
          },
    );

    const cddEcheance = salaries.filter((s) => {
      if (s.statut_contrat !== 'CDD' || !s.date_fin_contrat) return false;
      const fin = new Date(s.date_fin_contrat);
      const dans30j = new Date(Date.now() + 30 * 86_400_000);
      return fin <= dans30j && fin >= new Date();
    });
    r.push(
      cddEcheance.length === 0
        ? { test: 'CDD a echeance', level: 'ok', msg: 'Aucun CDD critique' }
        : {
            test: 'CDD a echeance',
            level: 'warn',
            msg: `${cddEcheance.length} CDD echeance < 30j`,
          },
    );

    // Bulletins sans charges (cas anormal)
    const sansCharges = bulletins.filter((b) => b.brut_imposable > 0 && b.total_charges_emp === 0);
    r.push(
      sansCharges.length === 0
        ? { test: 'Bulletins avec charges', level: 'ok', msg: 'Tous calcules' }
        : {
            test: 'Bulletins avec charges',
            level: 'fail',
            msg: `${sansCharges.length} bulletins sans charges`,
          },
    );

    return { agentName: this.NAME, results: r, durationMs: performance.now() - start };
  }
}
